import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import type { Group as KonvaGroupNode } from "konva/lib/Group";
import type { Transformer as KonvaTransformerNode } from "konva/lib/shapes/Transformer";
import {
  ArrowDown,
  ArrowUp,
  Archive,
  ChevronsDown,
  ChevronsUp,
  Copy,
  Download,
  History,
  MousePointer2,
  Plus,
  RotateCcw,
  Save,
  Send,
  Trash2,
  Upload,
} from "lucide-react";
import { Group as KonvaGroup, Rect as KonvaRect, Transformer } from "react-konva";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";
import {
  SOCIAL_POST_LAYOUT_TEMPLATE_AI_POLICY_MODES,
  SOCIAL_POST_LAYOUT_TEMPLATE_BRAND_COLOR_BIASES,
  SOCIAL_POST_LAYOUT_TEMPLATE_IMAGE_SLOT_KINDS,
  SOCIAL_POST_LAYOUT_TEMPLATE_SAFE_ZONE_ROLES,
  SOCIAL_POST_LAYOUT_TEMPLATE_SLOT_REQUIREMENTS,
  SOCIAL_POST_LAYOUT_TEMPLATE_TEXT_SLOT_KINDS,
  socialPostLayoutTemplateExportBundleSchema,
  type SocialPostLayoutTemplateExportBundle,
  type SocialPostLayoutTemplateLayerV2,
} from "@runcheetah/shared";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Skeleton,
} from "@runcheetah/ui";

import { EmptyState, QueryErrorState, Select, Textarea } from "@/components/common";
import {
  LayoutTemplateCanvasPreview,
  LayoutTemplateCanvasSurface,
  renderLayoutTemplateLayer,
} from "@/components/social-post-layout-template-canvas-preview";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import {
  LayoutTemplateUsageSummary,
  TemplateStat,
  getLayoutTemplateUsageMetric,
  type LayoutTemplateUsageMetric,
  type LayoutTemplateUsageMetrics,
} from "./layout-templates/usage-summary";

import {
  addLayoutTemplateImageSlot,
  addLayoutTemplateTextSlot,
  buildLayoutTemplateWriteInput,
  createEmptyLayoutTemplateDraft,
  createLayoutTemplateDraftFromOutput,
  createLayoutTemplateId,
  formatLayoutTemplateRawJsonDraft,
  getLayoutTemplateStatusLabel,
  getLayoutTemplatePatchFieldsForLayer,
  getSortedLayoutTemplateLayerEntries,
  getNextLayoutTemplateSortOrder,
  isLayoutTemplateLayerOutsideCanvas,
  LAYOUT_TEMPLATE_SUPPORTED_POST_TYPES,
  moveLayoutTemplateId,
  moveLayoutTemplateLayer,
  parseLayoutTemplateRawJsonDraft,
  removeLayoutTemplateImageSlot,
  removeLayoutTemplateTextSlot,
  reconcileLayoutTemplateDraftWithTemplates,
  REQUIRED_LAYOUT_TEMPLATE_POST_TYPE,
  resolveLayoutTemplateDraftAfterDelete,
  resolveLayoutTemplateLayerCanvasRecoveryFrame,
  resolveLayoutTemplateLayerDragFrame,
  resolveLayoutTemplateLayerNodeTransformFrame,
  updateLayoutTemplateLayerAiPolicy,
  updateLayoutTemplateLayerAiPolicyField,
  updateLayoutTemplateImageSlot,
  updateLayoutTemplateLayerBinding,
  updateLayoutTemplateLayerFrame,
  updateLayoutTemplateLogoSlot,
  updateLayoutTemplateTextSlot,
  updateLayoutTemplateLayerOptional,
  updateLayoutTemplateLayerText,
  updateLayoutTemplateTextLayerStyle,
  updateLayoutTemplateSupportedPostType,
  validateLayoutTemplateDraft,
  type LayoutTemplateAiPolicy,
  type LayoutTemplateDraft,
  type LayoutTemplateImageSlot,
  type LayoutTemplateLayerBinding,
  type LayoutTemplateLayerFrameUpdate,
  type LayoutTemplateLayerMoveDirection,
  type LayoutTemplateLogoSlot,
  type LayoutTemplatePatchField,
  type LayoutTemplateSupportedPostType,
  type LayoutTemplateTextLayerStyleUpdate,
  type LayoutTemplateTextSlot,
} from "./layout-templates-state";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type LayoutTemplate = RouterOutputs["admin"]["socialPostLayoutTemplates"]["list"][number];
type LayoutTemplateRevision =
  RouterOutputs["admin"]["socialPostLayoutTemplates"]["listRevisions"][number];
export {
  formatLayoutTemplateSelectionRate,
  getLayoutTemplateUsageMetric,
} from "./layout-templates/usage-summary";
export type {
  LayoutTemplateUsageMetric,
  LayoutTemplateUsageMetrics,
} from "./layout-templates/usage-summary";
type LayoutTemplateEditorTab = "overview" | "visual" | "slots" | "raw" | "publish";
type DraftFieldUpdater = <TKey extends keyof LayoutTemplateDraft>(
  key: TKey,
  value: LayoutTemplateDraft[TKey],
) => void;

const LAYOUT_TEMPLATE_EDITOR_TABS = [
  { value: "overview", label: "Overview" },
  { value: "visual", label: "Visual" },
  { value: "slots", label: "Slots" },
  { value: "raw", label: "Raw JSON" },
  { value: "publish", label: "Publishing" },
] as const satisfies readonly { value: LayoutTemplateEditorTab; label: string }[];

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
    {children}
  </span>
);

const StatusBadge = ({ status }: { status: LayoutTemplate["status"] }) => {
  const variant = status === "published" ? "default" : "secondary";

  return <Badge variant={variant}>{getLayoutTemplateStatusLabel(status)}</Badge>;
};

const formatDateTime = (value: string): string => new Date(value).toLocaleString();

const getDuplicateName = (template: LayoutTemplate): string => `${template.name} Copy`;

const getDuplicateTemplateId = (): string => createLayoutTemplateId();

export const getLayoutTemplateExportFileName = (exportedAt: string): string =>
  `layout-templates-${exportedAt.replace(/[:.]/g, "-")}.json`;

const downloadLayoutTemplateExportBundle = (bundle: SocialPostLayoutTemplateExportBundle): void => {
  const blob = new Blob([`${JSON.stringify(bundle, null, 2)}\n`], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getLayoutTemplateExportFileName(bundle.exportedAt);
  document.body.append(link);
  try {
    link.click();
  } finally {
    link.remove();
    URL.revokeObjectURL(url);
  }
};

type DraftUpdater = (updater: (current: LayoutTemplateDraft) => LayoutTemplateDraft) => void;
type LogoPlacement = LayoutTemplateLogoSlot["preferredPlacements"][number];

const LOGO_SLOT_PLACEMENTS = [
  "top_left",
  "top_right",
  "bottom_left",
  "bottom_right",
] as const satisfies readonly LogoPlacement[];

const formatOptionLabel = (value: string): string =>
  value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatPatchFieldLabel = (value: string): string =>
  formatOptionLabel(value.replace(/([a-z])([A-Z])/g, "$1 $2"));

const parseOptionalInteger = (value: string): number | undefined =>
  value.trim() === "" ? undefined : Number(value);

const LAYOUT_TEMPLATE_POST_TYPE_HELP: Record<LayoutTemplateSupportedPostType, string> = {
  feed_portrait: "Canonical 4:5 Instagram feed layout. Required by the template schema.",
  feed_square: "Square feed adaptation can use the canonical canvas as a source layout.",
  story: "Allows the planner to select this template for story generation.",
  tiktok_portrait: "Allows the planner to select this template for TikTok portrait generation.",
  tiktok_carousel_portrait:
    "Allows the planner to select this template for TikTok portrait carousel slides.",
};

const getLayerDisplayName = (layer: SocialPostLayoutTemplateLayerV2): string =>
  layer.layerName ?? layer.id;

const getLayerBindingValue = (binding: LayoutTemplateLayerBinding): string => {
  switch (binding.type) {
    case "textSlot":
      return `textSlot:${binding.slotId}`;
    case "imageSlot":
      return `imageSlot:${binding.slotId}:${binding.index}`;
    case "logoSlot":
      return "logoSlot";
    case "static":
      return "static";
  }
};

const parseLayerBindingValue = (value: string): LayoutTemplateLayerBinding => {
  if (value === "logoSlot") {
    return { type: "logoSlot" };
  }

  if (value === "static") {
    return { type: "static" };
  }

  const [type, slotId, index] = value.split(":");
  if (type === "textSlot" && slotId) {
    return { type: "textSlot", slotId };
  }

  if (type === "imageSlot" && slotId) {
    return { type: "imageSlot", slotId, index: Number(index ?? 0) };
  }

  return { type: "static" };
};

const SupportedPostTypesSection = ({
  draft,
  onDraftChange,
}: {
  draft: LayoutTemplateDraft;
  onDraftChange: DraftUpdater;
}) => (
  <div className="rounded-md border bg-muted/20 p-3">
    <div>
      <FieldLabel>Supported Post Types</FieldLabel>
      <p className="text-sm text-muted-foreground">
        Controls which planner targets may select this template. Seeded defaults stay unchanged
        until an admin saves a draft.
      </p>
    </div>
    <div className="mt-3 grid gap-2 md:grid-cols-3">
      {LAYOUT_TEMPLATE_SUPPORTED_POST_TYPES.map((postType) => {
        const required = postType === REQUIRED_LAYOUT_TEMPLATE_POST_TYPE;
        return (
          <label key={postType} className="rounded-md border bg-background p-3 text-sm">
            <span className="flex items-center gap-2 font-medium text-foreground">
              <input
                type="checkbox"
                className="size-4 rounded border-input disabled:opacity-50"
                checked={draft.template.supportedPostTypes.includes(postType)}
                disabled={required}
                onChange={(event) =>
                  onDraftChange((current) =>
                    updateLayoutTemplateSupportedPostType(current, postType, event.target.checked),
                  )
                }
              />
              {formatOptionLabel(postType)}
            </span>
            <span className="mt-2 block text-xs leading-5 text-muted-foreground">
              {LAYOUT_TEMPLATE_POST_TYPE_HELP[postType]}
            </span>
          </label>
        );
      })}
    </div>
  </div>
);

const LayoutTemplateOverviewSection = ({
  draft,
  onDraftFieldChange,
  onDraftChange,
}: {
  draft: LayoutTemplateDraft;
  onDraftFieldChange: DraftFieldUpdater;
  onDraftChange: DraftUpdater;
}) => {
  const canvas = draft.template.canonicalScene.canvas;
  const textSlotCount = draft.template.slots.textSlots.length;
  const imageSlotCount = draft.template.slots.imageSlots.length;

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-xl border bg-gradient-to-br from-primary/10 via-muted/40 to-background p-4 shadow-xs">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 space-y-2">
            <FieldLabel>Template Brief</FieldLabel>
            <h2 className="max-w-3xl text-2xl font-semibold tracking-tight text-foreground">
              {draft.name || "Untitled layout template"}
            </h2>
            <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
              {draft.description ||
                "Describe the layout promise, when planners should choose it, and what content it expects."}
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <StatusBadge status={draft.status} />
            <code className="rounded-md border bg-background/80 px-2 py-1 text-xs text-muted-foreground">
              {draft.templateId}
            </code>
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <TemplateStat label="Canvas" value={`${canvas.width}x${canvas.height}`} />
          <TemplateStat label="Scene Layers" value={draft.template.canonicalScene.layers.length} />
          <TemplateStat
            label="Content Slots"
            value={`${textSlotCount} text / ${imageSlotCount} image`}
          />
          <TemplateStat label="Sort Order" value={draft.sortOrder} />
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]">
        <div className="space-y-4">
          <div className="rounded-xl border bg-background/80 p-4 shadow-xs">
            <div className="mb-4">
              <FieldLabel>Identity</FieldLabel>
              <p className="mt-1 text-sm text-muted-foreground">
                Name the template like an internal product: specific, searchable, and tied to the
                creative outcome.
              </p>
            </div>
            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px]">
              <label className="space-y-1.5">
                <FieldLabel>Name</FieldLabel>
                <Input
                  value={draft.name}
                  onChange={(event) => onDraftFieldChange("name", event.target.value)}
                />
              </label>
              <label className="space-y-1.5">
                <FieldLabel>Sort Order</FieldLabel>
                <Input
                  type="number"
                  min={0}
                  value={draft.sortOrder}
                  onChange={(event) => onDraftFieldChange("sortOrder", Number(event.target.value))}
                />
              </label>
            </div>
            <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.7fr)]">
              <label className="space-y-1.5">
                <FieldLabel>Description</FieldLabel>
                <Textarea
                  value={draft.description}
                  className="min-h-28"
                  onChange={(event) => onDraftFieldChange("description", event.target.value)}
                />
              </label>
              <label className="space-y-1.5">
                <FieldLabel>Template ID</FieldLabel>
                <Input
                  value={draft.templateId}
                  className="font-mono"
                  disabled={Boolean(draft.id)}
                  onChange={(event) => onDraftFieldChange("templateId", event.target.value)}
                />
              </label>
            </div>
          </div>

          <SupportedPostTypesSection draft={draft} onDraftChange={onDraftChange} />
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border bg-background/80 p-4 shadow-xs">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <FieldLabel>Template Preview</FieldLabel>
                <p className="mt-1 text-sm text-muted-foreground">
                  View-only snapshot of the current draft layout.
                </p>
              </div>
              <Badge variant="secondary">View-only</Badge>
            </div>
            <LayoutTemplateCanvasPreview
              template={draft.template}
              label={`View-only preview of ${draft.name || "Untitled layout template"}`}
              className="mx-auto w-full max-w-72"
              maxWidth={288}
            />
          </div>

          <div className="rounded-xl border bg-background/80 p-4 shadow-xs">
            <div className="mb-4">
              <FieldLabel>Planner Selection Signals</FieldLabel>
              <p className="mt-1 text-sm text-muted-foreground">
                These cues help template selection prefer the right layout before generation begins.
              </p>
            </div>
            <label className="block space-y-1.5">
              <FieldLabel>Best For</FieldLabel>
              <Textarea
                value={draft.bestForText}
                className="min-h-32"
                placeholder="One selection cue per line"
                onChange={(event) => onDraftFieldChange("bestForText", event.target.value)}
              />
            </label>
            <label className="mt-3 block space-y-1.5">
              <FieldLabel>Avoid When</FieldLabel>
              <Textarea
                value={draft.avoidWhenText}
                className="min-h-32"
                placeholder="One exclusion cue per line"
                onChange={(event) => onDraftFieldChange("avoidWhenText", event.target.value)}
              />
            </label>
          </div>

          <div className="rounded-xl border border-dashed bg-muted/20 p-4 text-sm text-muted-foreground">
            <FieldLabel>Authoring Flow</FieldLabel>
            <p className="mt-2 leading-6">
              Start with the overview contract, move to the visual workbench for layer geometry,
              bindings, and policy, then publish an immutable revision.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const TextSlotEditor = ({
  slot,
  index,
  canRemove,
  disabled = false,
  showRemoveAction = true,
  onUpdate,
  onRemove,
}: {
  slot: LayoutTemplateTextSlot;
  index: number;
  canRemove: boolean;
  disabled?: boolean;
  showRemoveAction?: boolean;
  onUpdate: (index: number, patch: Partial<LayoutTemplateTextSlot>) => void;
  onRemove: (index: number) => void;
}) => (
  <div className="rounded-md border bg-background p-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <div className="text-sm font-medium">{slot.label}</div>
        <div className="font-mono text-xs text-muted-foreground">{slot.id}</div>
      </div>
      {showRemoveAction ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled || !canRemove}
          onClick={() => onRemove(index)}
        >
          <Trash2 className="size-3.5" />
          Remove
        </Button>
      ) : null}
    </div>
    <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      <label className="space-y-1.5">
        <FieldLabel>Text Slot ID</FieldLabel>
        <Input
          value={slot.id}
          className="font-mono"
          disabled={disabled}
          onChange={(event) => onUpdate(index, { id: event.target.value })}
        />
      </label>
      <label className="space-y-1.5">
        <FieldLabel>Label</FieldLabel>
        <Input
          value={slot.label}
          disabled={disabled}
          onChange={(event) => onUpdate(index, { label: event.target.value })}
        />
      </label>
      <label className="space-y-1.5">
        <FieldLabel>Kind</FieldLabel>
        <Select
          value={slot.kind}
          disabled={disabled}
          onChange={(event) =>
            onUpdate(index, { kind: event.target.value as LayoutTemplateTextSlot["kind"] })
          }
        >
          {SOCIAL_POST_LAYOUT_TEMPLATE_TEXT_SLOT_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {formatOptionLabel(kind)}
            </option>
          ))}
        </Select>
      </label>
      <label className="space-y-1.5">
        <FieldLabel>Requirement</FieldLabel>
        <Select
          value={slot.requirement}
          disabled={disabled}
          onChange={(event) =>
            onUpdate(index, {
              requirement: event.target.value as LayoutTemplateTextSlot["requirement"],
            })
          }
        >
          {SOCIAL_POST_LAYOUT_TEMPLATE_SLOT_REQUIREMENTS.map((requirement) => (
            <option key={requirement} value={requirement}>
              {formatOptionLabel(requirement)}
            </option>
          ))}
        </Select>
      </label>
    </div>
    <div className="mt-3 grid gap-3 md:grid-cols-[160px_minmax(0,1fr)]">
      <label className="space-y-1.5">
        <FieldLabel>Max Length</FieldLabel>
        <Input
          type="number"
          min={0}
          max={1000}
          value={slot.suggestedMaxLength ?? ""}
          disabled={disabled}
          onChange={(event) =>
            onUpdate(index, { suggestedMaxLength: parseOptionalInteger(event.target.value) })
          }
        />
      </label>
      <label className="space-y-1.5">
        <FieldLabel>Tone Hint</FieldLabel>
        <Input
          value={slot.toneHint ?? ""}
          disabled={disabled}
          onChange={(event) => onUpdate(index, { toneHint: event.target.value || undefined })}
        />
      </label>
    </div>
    <label className="mt-3 block space-y-1.5">
      <FieldLabel>Purpose</FieldLabel>
      <Textarea
        value={slot.purpose}
        className="min-h-20"
        disabled={disabled}
        onChange={(event) => onUpdate(index, { purpose: event.target.value })}
      />
    </label>
  </div>
);

const ImageSlotEditor = ({
  slot,
  index,
  canRemove,
  disabled = false,
  showRemoveAction = true,
  onUpdate,
  onRemove,
}: {
  slot: LayoutTemplateImageSlot;
  index: number;
  canRemove: boolean;
  disabled?: boolean;
  showRemoveAction?: boolean;
  onUpdate: (index: number, patch: Partial<LayoutTemplateImageSlot>) => void;
  onRemove: (index: number) => void;
}) => (
  <div className="rounded-md border bg-background p-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <div className="text-sm font-medium">{slot.label}</div>
        <div className="font-mono text-xs text-muted-foreground">{slot.id}</div>
      </div>
      {showRemoveAction ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled || !canRemove}
          onClick={() => onRemove(index)}
        >
          <Trash2 className="size-3.5" />
          Remove
        </Button>
      ) : null}
    </div>
    <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      <label className="space-y-1.5">
        <FieldLabel>Image Slot ID</FieldLabel>
        <Input
          value={slot.id}
          className="font-mono"
          disabled={disabled}
          onChange={(event) => onUpdate(index, { id: event.target.value })}
        />
      </label>
      <label className="space-y-1.5">
        <FieldLabel>Label</FieldLabel>
        <Input
          value={slot.label}
          disabled={disabled}
          onChange={(event) => onUpdate(index, { label: event.target.value })}
        />
      </label>
      <label className="space-y-1.5">
        <FieldLabel>Kind</FieldLabel>
        <Select
          value={slot.kind}
          disabled={disabled}
          onChange={(event) =>
            onUpdate(index, { kind: event.target.value as LayoutTemplateImageSlot["kind"] })
          }
        >
          {SOCIAL_POST_LAYOUT_TEMPLATE_IMAGE_SLOT_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {formatOptionLabel(kind)}
            </option>
          ))}
        </Select>
      </label>
      <label className="space-y-1.5">
        <FieldLabel>Requirement</FieldLabel>
        <Select
          value={slot.requirement}
          disabled={disabled}
          onChange={(event) =>
            onUpdate(index, {
              requirement: event.target.value as LayoutTemplateImageSlot["requirement"],
            })
          }
        >
          {SOCIAL_POST_LAYOUT_TEMPLATE_SLOT_REQUIREMENTS.map((requirement) => (
            <option key={requirement} value={requirement}>
              {formatOptionLabel(requirement)}
            </option>
          ))}
        </Select>
      </label>
    </div>
    <div className="mt-3 grid gap-3 md:grid-cols-3">
      <label className="space-y-1.5">
        <FieldLabel>Min Count</FieldLabel>
        <Input
          type="number"
          min={0}
          max={6}
          value={slot.minCount}
          disabled={disabled}
          onChange={(event) => onUpdate(index, { minCount: Number(event.target.value) })}
        />
      </label>
      <label className="space-y-1.5">
        <FieldLabel>Max Count</FieldLabel>
        <Input
          type="number"
          min={0}
          max={6}
          value={slot.maxCount}
          disabled={disabled}
          onChange={(event) => onUpdate(index, { maxCount: Number(event.target.value) })}
        />
      </label>
      <label className="space-y-1.5">
        <FieldLabel>Safe Zone Role</FieldLabel>
        <Select
          value={slot.safeZoneRole}
          disabled={disabled}
          onChange={(event) =>
            onUpdate(index, {
              safeZoneRole: event.target.value as LayoutTemplateImageSlot["safeZoneRole"],
            })
          }
        >
          {SOCIAL_POST_LAYOUT_TEMPLATE_SAFE_ZONE_ROLES.map((role) => (
            <option key={role} value={role}>
              {formatOptionLabel(role)}
            </option>
          ))}
        </Select>
      </label>
    </div>
    <label className="mt-3 block space-y-1.5">
      <FieldLabel>Purpose</FieldLabel>
      <Textarea
        value={slot.purpose}
        className="min-h-20"
        disabled={disabled}
        onChange={(event) => onUpdate(index, { purpose: event.target.value })}
      />
    </label>
  </div>
);

const LogoSlotEditor = ({
  slot,
  disabled = false,
  onUpdate,
}: {
  slot: LayoutTemplateLogoSlot;
  disabled?: boolean;
  onUpdate: (patch: Partial<LayoutTemplateLogoSlot>) => void;
}) => {
  const updatePlacement = (placement: LogoPlacement, checked: boolean) => {
    const selected = new Set(slot.preferredPlacements);
    if (checked) {
      selected.add(placement);
    } else {
      selected.delete(placement);
    }

    onUpdate({ preferredPlacements: LOGO_SLOT_PLACEMENTS.filter((item) => selected.has(item)) });
  };

  return (
    <div className="rounded-md border bg-background p-3">
      <div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
        <label className="space-y-1.5">
          <FieldLabel>Logo Requirement</FieldLabel>
          <Select
            value={slot.requirement}
            disabled={disabled}
            onChange={(event) =>
              onUpdate({ requirement: event.target.value as LayoutTemplateLogoSlot["requirement"] })
            }
          >
            {SOCIAL_POST_LAYOUT_TEMPLATE_SLOT_REQUIREMENTS.map((requirement) => (
              <option key={requirement} value={requirement}>
                {formatOptionLabel(requirement)}
              </option>
            ))}
          </Select>
        </label>
        <label className="space-y-1.5">
          <FieldLabel>Logo Purpose</FieldLabel>
          <Input
            value={slot.purpose}
            disabled={disabled}
            onChange={(event) => onUpdate({ purpose: event.target.value })}
          />
        </label>
      </div>
      <div className="mt-3 space-y-2">
        <FieldLabel>Preferred Placements</FieldLabel>
        <div className="flex flex-wrap gap-3">
          {LOGO_SLOT_PLACEMENTS.map((placement) => (
            <label
              key={placement}
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <input
                type="checkbox"
                className="size-4 rounded border-input disabled:opacity-50"
                checked={slot.preferredPlacements.includes(placement)}
                disabled={disabled}
                onChange={(event) => updatePlacement(placement, event.target.checked)}
              />
              {formatOptionLabel(placement)}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

const SlotEditorSection = ({
  draft,
  onDraftChange,
}: {
  draft: LayoutTemplateDraft;
  onDraftChange: DraftUpdater;
}) => (
  <div className="rounded-md border bg-muted/20 p-3">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <FieldLabel>Slots</FieldLabel>
        <p className="text-sm text-muted-foreground">
          Define the planner-facing content contract that layers can bind to.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onDraftChange(addLayoutTemplateTextSlot)}
        >
          <Plus className="size-3.5" />
          Text slot
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onDraftChange(addLayoutTemplateImageSlot)}
        >
          <Plus className="size-3.5" />
          Image slot
        </Button>
      </div>
    </div>

    <div className="mt-4 space-y-3">
      <div className="text-sm font-medium">Text slots</div>
      {draft.template.slots.textSlots.map((slot, index) => (
        <TextSlotEditor
          key={draft.slotKeys.textSlots[index] ?? slot.id}
          slot={slot}
          index={index}
          canRemove={draft.template.slots.textSlots.length > 1}
          onUpdate={(slotIndex, patch) =>
            onDraftChange((current) => updateLayoutTemplateTextSlot(current, slotIndex, patch))
          }
          onRemove={(slotIndex) =>
            onDraftChange((current) => removeLayoutTemplateTextSlot(current, slotIndex))
          }
        />
      ))}
    </div>

    <div className="mt-4 space-y-3">
      <div className="text-sm font-medium">Image slots</div>
      {draft.template.slots.imageSlots.map((slot, index) => (
        <ImageSlotEditor
          key={draft.slotKeys.imageSlots[index] ?? slot.id}
          slot={slot}
          index={index}
          canRemove={draft.template.slots.imageSlots.length > 1}
          onUpdate={(slotIndex, patch) =>
            onDraftChange((current) => updateLayoutTemplateImageSlot(current, slotIndex, patch))
          }
          onRemove={(slotIndex) =>
            onDraftChange((current) => removeLayoutTemplateImageSlot(current, slotIndex))
          }
        />
      ))}
    </div>

    <div className="mt-4 space-y-3">
      <div className="text-sm font-medium">Logo slot</div>
      <LogoSlotEditor
        slot={draft.template.slots.logoSlot}
        onUpdate={(patch) =>
          onDraftChange((current) => updateLayoutTemplateLogoSlot(current, patch))
        }
      />
    </div>
  </div>
);

const LayoutTemplateRawJsonSection = ({
  rawJson,
  error,
  disabled,
  onRawJsonChange,
  onReset,
}: {
  rawJson: string;
  error: string | null;
  disabled: boolean;
  onRawJsonChange: (value: string) => void;
  onReset: () => void;
}) => (
  <div className="space-y-3 rounded-md border bg-muted/20 p-3">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <FieldLabel>Raw JSON</FieldLabel>
        <p className="mt-1 text-sm text-muted-foreground">
          Edit the draft save payload directly. Valid JSON updates the draft in the other tabs; save
          or publish to persist it.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={error ? "destructive" : "default"}>{error ? "Invalid" : "Valid"}</Badge>
        <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={onReset}>
          <RotateCcw className="size-3.5" />
          Reformat JSON
        </Button>
      </div>
    </div>
    <Textarea
      aria-label="Layout template raw JSON"
      value={rawJson}
      spellCheck={false}
      disabled={disabled}
      className="min-h-[36rem] resize-y font-mono text-xs leading-5"
      onChange={(event) => onRawJsonChange(event.target.value)}
    />
    {error ? (
      <p
        role="alert"
        className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
      >
        <span className="font-medium">Raw JSON error:</span> {error}
      </p>
    ) : (
      <p className="text-xs leading-5 text-muted-foreground">
        Paste either the full save payload with <code>template</code> and <code>sortOrder</code>, or
        a bare layout template JSON object. Existing templates cannot change their template ID.
      </p>
    )}
  </div>
);

const getLayerBindingOptions = (
  draft: LayoutTemplateDraft,
  layer: SocialPostLayoutTemplateLayerV2,
): Array<{ value: string; label: string }> => {
  const options = [{ value: "static", label: "Static" }];

  if (layer.type === "text") {
    options.push(
      ...draft.template.slots.textSlots.map((slot) => ({
        value: `textSlot:${slot.id}`,
        label: `Text: ${slot.label}`,
      })),
    );
  }

  if (layer.type === "image") {
    for (const slot of draft.template.slots.imageSlots) {
      const bindingCount = Math.max(slot.maxCount, 1);
      for (let index = 0; index < bindingCount; index += 1) {
        options.push({
          value: `imageSlot:${slot.id}:${index}`,
          label: `Image: ${slot.label} [${index}]`,
        });
      }
    }
  }

  if (layer.type === "logo") {
    options.push({ value: "logoSlot", label: "Logo slot" });
  }

  const currentValue = getLayerBindingValue(layer.binding);
  if (!options.some((option) => option.value === currentValue)) {
    options.push({ value: currentValue, label: `Current: ${currentValue}` });
  }

  return options;
};

const LayerAiPolicyEditor = ({
  layer,
  disabled = false,
  onDraftChange,
}: {
  layer: SocialPostLayoutTemplateLayerV2;
  disabled?: boolean;
  onDraftChange: DraftUpdater;
}) => {
  const layerName = getLayerDisplayName(layer);
  const patchFields = getLayoutTemplatePatchFieldsForLayer(layer, layer.aiPolicy.mode);
  const allowedFields = new Set(layer.aiPolicy.allowedFields);
  const canPatchVisibility = Boolean(layer.optional && layer.aiPolicy.allowHideWhenOptional);

  const updatePolicy = (patch: Partial<LayoutTemplateAiPolicy>) =>
    onDraftChange((current) => updateLayoutTemplateLayerAiPolicy(current, layer.id, patch));

  const updateField = (field: LayoutTemplatePatchField, enabled: boolean) =>
    onDraftChange((current) =>
      updateLayoutTemplateLayerAiPolicyField(current, layer.id, field, enabled),
    );

  return (
    <div
      role="group"
      aria-label={`AI policy controls for ${layerName}`}
      className="rounded-md border bg-background p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{layerName}</span>
        <Badge variant="secondary">{layer.type}</Badge>
        <span className="font-mono text-xs text-muted-foreground">{layer.id}</span>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <label className="space-y-1.5">
          <FieldLabel>Policy Mode</FieldLabel>
          <Select
            aria-label={`AI policy mode for ${layerName}`}
            value={layer.aiPolicy.mode}
            disabled={disabled}
            onChange={(event) =>
              updatePolicy({ mode: event.target.value as LayoutTemplateAiPolicy["mode"] })
            }
          >
            {SOCIAL_POST_LAYOUT_TEMPLATE_AI_POLICY_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {formatOptionLabel(mode)}
              </option>
            ))}
          </Select>
        </label>
        <label className="space-y-1.5">
          <FieldLabel>Brand Color Bias</FieldLabel>
          <Select
            aria-label={`Brand color bias for ${layerName}`}
            value={layer.aiPolicy.brandColorBias ?? "strong"}
            disabled={disabled}
            onChange={(event) =>
              updatePolicy({
                brandColorBias: event.target.value as LayoutTemplateAiPolicy["brandColorBias"],
              })
            }
          >
            {SOCIAL_POST_LAYOUT_TEMPLATE_BRAND_COLOR_BIASES.map((bias) => (
              <option key={bias} value={bias}>
                {formatOptionLabel(bias)}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            className="size-4 rounded border-input"
            checked={layer.optional}
            disabled={disabled}
            onChange={(event) =>
              onDraftChange((current) =>
                updateLayoutTemplateLayerOptional(current, layer.id, event.target.checked),
              )
            }
          />
          Optional layer
        </label>
        <label className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            className="size-4 rounded border-input disabled:opacity-50"
            disabled={disabled || !layer.optional}
            checked={Boolean(layer.aiPolicy.allowHideWhenOptional)}
            onChange={(event) => updatePolicy({ allowHideWhenOptional: event.target.checked })}
          />
          AI may hide optional layer
        </label>
      </div>
      <div className="mt-3 space-y-2">
        <FieldLabel>Allowed Patch Fields</FieldLabel>
        {patchFields.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            This policy mode does not allow geometric or style patches.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {patchFields.map((field) => {
              const fieldDisabled = field === "visible" && !canPatchVisibility;
              return (
                <label
                  key={field}
                  className={cn(
                    "flex items-center gap-2 rounded-md border px-3 py-2 text-sm",
                    fieldDisabled ? "text-muted-foreground/60" : "text-muted-foreground",
                  )}
                >
                  <input
                    type="checkbox"
                    className="size-4 rounded border-input disabled:opacity-50"
                    disabled={disabled || fieldDisabled}
                    checked={allowedFields.has(field) && !fieldDisabled}
                    onChange={(event) => updateField(field, event.target.checked)}
                  />
                  {formatPatchFieldLabel(field)}
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

type EditableLayoutTemplateLayer = SocialPostLayoutTemplateLayerV2;
type LayoutTemplateTextLayer = Extract<EditableLayoutTemplateLayer, { type: "text" }>;

const MIN_TRANSFORM_SIZE = 12;

const layoutTemplateLayerTypeLabel: Record<EditableLayoutTemplateLayer["type"], string> = {
  frame: "Frame",
  image: "Image",
  logo: "Logo",
  rect: "Rect",
  shape: "Shape",
  text: "Text",
};

const findLayoutTemplateLayer = (
  draft: LayoutTemplateDraft,
  layerId: string | null,
): EditableLayoutTemplateLayer | null => {
  if (!layerId) {
    return null;
  }

  return draft.template.canonicalScene.layers.find((layer) => layer.id === layerId) ?? null;
};

const resolveNumberInputValue = (value: number): string =>
  Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);

const CanvasNumberField = ({
  label,
  value,
  disabled,
  min,
  onChange,
}: {
  label: string;
  value: number;
  disabled: boolean;
  min?: number;
  onChange: (value: number) => void;
}) => (
  <label className="space-y-1.5">
    <FieldLabel>{label}</FieldLabel>
    <Input
      type="number"
      value={resolveNumberInputValue(value)}
      min={min}
      disabled={disabled}
      onChange={(event) => {
        const nextValue = Number(event.target.value);
        if (Number.isFinite(nextValue)) {
          onChange(nextValue);
        }
      }}
    />
  </label>
);

const EditableLayoutTemplateLayerNode = ({
  layer,
  scale,
  selected,
  disabled,
  editing,
  onSelect,
  onStartTextEdit,
  onFrameChange,
}: {
  layer: EditableLayoutTemplateLayer;
  scale: number;
  selected: boolean;
  disabled: boolean;
  editing: boolean;
  onSelect: (layerId: string) => void;
  onStartTextEdit: (layerId: string) => void;
  onFrameChange: (layerId: string, changes: LayoutTemplateLayerFrameUpdate) => void;
}) => {
  const groupRef = useRef<KonvaGroupNode | null>(null);
  const transformerRef = useRef<KonvaTransformerNode | null>(null);
  const [transformPreviewFrame, setTransformPreviewFrame] =
    useState<Required<LayoutTemplateLayerFrameUpdate> | null>(null);
  const displayLayer =
    layer.type === "text" && transformPreviewFrame ? { ...layer, ...transformPreviewFrame } : layer;

  useEffect(() => {
    const transformer = transformerRef.current;
    const group = groupRef.current;
    if (!transformer || !group || !selected || !layer.visible) {
      return;
    }

    transformer.nodes([group]);
    transformer.forceUpdate();
    transformer.getLayer()?.batchDraw();
  }, [layer.id, layer.visible, layer.height, layer.rotation, layer.width, scale, selected]);

  useEffect(() => {
    if (!selected) {
      setTransformPreviewFrame(null);
    }
  }, [selected]);

  const selectLayer = (event: { cancelBubble: boolean }) => {
    event.cancelBubble = true;
    onSelect(layer.id);
  };

  return (
    <>
      <KonvaGroup
        ref={groupRef}
        x={displayLayer.x * scale}
        y={displayLayer.y * scale}
        width={displayLayer.width * scale}
        height={displayLayer.height * scale}
        rotation={displayLayer.rotation}
        opacity={displayLayer.opacity}
        visible={displayLayer.visible}
        draggable={!disabled && !editing && displayLayer.visible}
        onMouseDown={selectLayer}
        onTouchStart={selectLayer}
        onClick={selectLayer}
        onTap={selectLayer}
        onDblClick={(event) => {
          selectLayer(event);
          if (!disabled && layer.type === "text") {
            onStartTextEdit(layer.id);
          }
        }}
        onDblTap={(event) => {
          selectLayer(event);
          if (!disabled && layer.type === "text") {
            onStartTextEdit(layer.id);
          }
        }}
        onDragStart={selectLayer}
        onDragEnd={(event) => {
          const target = event.target;
          onFrameChange(
            layer.id,
            resolveLayoutTemplateLayerDragFrame({
              dragX: target.x(),
              dragY: target.y(),
              scale,
            }),
          );
          target.getLayer()?.batchDraw();
        }}
        onTransformEnd={(event) => {
          const target = event.target;
          const frame = resolveLayoutTemplateLayerNodeTransformFrame({
            x: target.x(),
            y: target.y(),
            width: target.width(),
            height: target.height(),
            scaleX: target.scaleX(),
            scaleY: target.scaleY(),
            rotation: target.rotation(),
            scale,
          });

          onFrameChange(layer.id, frame);
          setTransformPreviewFrame(null);
          target.scaleX(1);
          target.scaleY(1);
          target.getLayer()?.batchDraw();
        }}
        onTransform={(event) => {
          if (layer.type !== "text") {
            return;
          }

          const target = event.target;
          setTransformPreviewFrame(
            resolveLayoutTemplateLayerNodeTransformFrame({
              x: target.x(),
              y: target.y(),
              width: target.width(),
              height: target.height(),
              scaleX: target.scaleX(),
              scaleY: target.scaleY(),
              rotation: target.rotation(),
              scale,
            }),
          );
          target.scaleX(1);
          target.scaleY(1);
          target.getLayer()?.batchDraw();
        }}
      >
        <KonvaRect
          x={0}
          y={0}
          width={displayLayer.width * scale}
          height={displayLayer.height * scale}
          fill="rgba(0,0,0,0.001)"
          strokeEnabled={false}
          listening={false}
        />
        {renderLayoutTemplateLayer(displayLayer, scale)}
        {selected ? (
          <KonvaRect
            x={0}
            y={0}
            width={displayLayer.width * scale}
            height={displayLayer.height * scale}
            stroke="#16a34a"
            strokeWidth={2}
            dash={[8, 6]}
            listening={false}
          />
        ) : null}
      </KonvaGroup>
      {selected && displayLayer.visible && !disabled && !editing ? (
        <Transformer
          ref={transformerRef}
          rotateEnabled
          flipEnabled={false}
          enabledAnchors={["top-left", "top-right", "bottom-left", "bottom-right"]}
          boundBoxFunc={(oldBox, newBox) =>
            newBox.width < MIN_TRANSFORM_SIZE || newBox.height < MIN_TRANSFORM_SIZE
              ? oldBox
              : newBox
          }
        />
      ) : null}
    </>
  );
};

const SelectedLayerEmptyState = () => (
  <div className="rounded-md border bg-background/75 p-4 text-sm text-muted-foreground">
    Select a layer on the canvas or in the layer stack to edit its geometry and text.
  </div>
);

const SelectedLayerHeader = ({
  layer,
  outsideCanvas,
  disabled,
  onBringIntoCanvas,
}: {
  layer: EditableLayoutTemplateLayer;
  outsideCanvas: boolean;
  disabled: boolean;
  onBringIntoCanvas: () => void;
}) => (
  <div className="flex flex-wrap items-start justify-between gap-3">
    <div>
      <FieldLabel>Selected Layer</FieldLabel>
      <div className="mt-1 font-mono text-xs font-semibold text-foreground">{layer.id}</div>
    </div>
    <div className="flex flex-wrap items-center justify-end gap-2">
      {outsideCanvas ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={onBringIntoCanvas}
        >
          <RotateCcw className="size-3.5" />
          Bring into canvas
        </Button>
      ) : null}
      <Badge variant="secondary">{layoutTemplateLayerTypeLabel[layer.type]}</Badge>
    </div>
  </div>
);

const LayerGeometryControls = ({
  layer,
  disabled,
  onFrameChange,
}: {
  layer: EditableLayoutTemplateLayer;
  disabled: boolean;
  onFrameChange: (changes: LayoutTemplateLayerFrameUpdate) => void;
}) => (
  <div className="grid grid-cols-2 gap-3">
    <CanvasNumberField
      label="X"
      value={layer.x}
      disabled={disabled}
      onChange={(x) => onFrameChange({ x })}
    />
    <CanvasNumberField
      label="Y"
      value={layer.y}
      disabled={disabled}
      onChange={(y) => onFrameChange({ y })}
    />
    <CanvasNumberField
      label="Width"
      value={layer.width}
      min={8}
      disabled={disabled}
      onChange={(width) => onFrameChange({ width: Math.max(8, width) })}
    />
    <CanvasNumberField
      label="Height"
      value={layer.height}
      min={8}
      disabled={disabled}
      onChange={(height) => onFrameChange({ height: Math.max(8, height) })}
    />
    <CanvasNumberField
      label="Rotation"
      value={layer.rotation}
      disabled={disabled}
      onChange={(rotation) => onFrameChange({ rotation })}
    />
  </div>
);

const LayerTextControls = ({
  layer,
  disabled,
  onTextChange,
  onStyleChange,
}: {
  layer: LayoutTemplateTextLayer;
  disabled: boolean;
  onTextChange: (text: string) => void;
  onStyleChange: (changes: LayoutTemplateTextLayerStyleUpdate) => void;
}) => {
  const hasStroke = layer.stroke !== undefined;
  const textStrokeWidth = layer.strokeWidth ?? 0;

  return (
    <div className="space-y-3">
      <label className="space-y-1.5">
        <FieldLabel>Text</FieldLabel>
        <Textarea
          value={layer.text}
          rows={5}
          disabled={disabled}
          onChange={(event) => onTextChange(event.target.value)}
        />
      </label>
      <div className="rounded-md border bg-muted/20 p-3">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            className="size-4 rounded border-input disabled:opacity-50"
            checked={hasStroke}
            disabled={disabled}
            onChange={(event) =>
              onStyleChange(
                event.target.checked
                  ? {
                      stroke: layer.stroke ?? "#171717",
                      strokeWidth: Math.max(textStrokeWidth, 2),
                    }
                  : { stroke: undefined, strokeWidth: 0 },
              )
            }
          />
          Enable text stroke
        </label>
        <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_8rem]">
          <label className="space-y-1.5">
            <FieldLabel>Stroke</FieldLabel>
            <Input
              value={layer.stroke ?? ""}
              placeholder="#171717"
              disabled={disabled || !hasStroke}
              onChange={(event) => onStyleChange({ stroke: event.target.value || "#171717" })}
            />
          </label>
          <CanvasNumberField
            label="Width"
            value={textStrokeWidth}
            min={0}
            disabled={disabled || !hasStroke}
            onChange={(value) => onStyleChange({ strokeWidth: Math.max(0, value) })}
          />
        </div>
      </div>
    </div>
  );
};

const SelectedLayerSlotControls = ({
  draft,
  layer,
  disabled,
  onDraftChange,
}: {
  draft: LayoutTemplateDraft;
  layer: EditableLayoutTemplateLayer;
  disabled: boolean;
  onDraftChange: DraftUpdater;
}) => {
  const bindingOptions = getLayerBindingOptions(draft, layer);
  const currentBindingValue = getLayerBindingValue(layer.binding);
  const currentBindingLabel =
    bindingOptions.find((option) => option.value === currentBindingValue)?.label ??
    currentBindingValue;

  const updateBinding = (binding: LayoutTemplateLayerBinding) => {
    onDraftChange((current) => updateLayoutTemplateLayerBinding(current, layer.id, binding));
  };

  const createAndBindSlot = () => {
    if (layer.type === "text") {
      onDraftChange((current) => {
        const next = addLayoutTemplateTextSlot(current);
        const slot = next.template.slots.textSlots.at(-1);
        return slot
          ? updateLayoutTemplateLayerBinding(next, layer.id, { type: "textSlot", slotId: slot.id })
          : current;
      });
      return;
    }

    if (layer.type === "image") {
      onDraftChange((current) => {
        const next = addLayoutTemplateImageSlot(current);
        const slot = next.template.slots.imageSlots.at(-1);
        return slot
          ? updateLayoutTemplateLayerBinding(next, layer.id, {
              type: "imageSlot",
              slotId: slot.id,
              index: 0,
            })
          : current;
      });
    }
  };

  const slotFields = (() => {
    const binding = layer.binding;

    if (binding.type === "textSlot") {
      const slotIndex = draft.template.slots.textSlots.findIndex(
        (slot) => slot.id === binding.slotId,
      );
      const slot = draft.template.slots.textSlots[slotIndex];
      if (!slot) {
        return <p className="text-xs text-destructive">The bound text slot no longer exists.</p>;
      }

      return (
        <TextSlotEditor
          slot={slot}
          index={slotIndex}
          canRemove={false}
          disabled={disabled}
          showRemoveAction={false}
          onUpdate={(index, patch) =>
            onDraftChange((current) => updateLayoutTemplateTextSlot(current, index, patch))
          }
          onRemove={() => undefined}
        />
      );
    }

    if (binding.type === "imageSlot") {
      const slotIndex = draft.template.slots.imageSlots.findIndex(
        (slot) => slot.id === binding.slotId,
      );
      const slot = draft.template.slots.imageSlots[slotIndex];
      if (!slot) {
        return <p className="text-xs text-destructive">The bound image slot no longer exists.</p>;
      }

      return (
        <ImageSlotEditor
          slot={slot}
          index={slotIndex}
          canRemove={false}
          disabled={disabled}
          showRemoveAction={false}
          onUpdate={(index, patch) =>
            onDraftChange((current) => updateLayoutTemplateImageSlot(current, index, patch))
          }
          onRemove={() => undefined}
        />
      );
    }

    if (binding.type === "logoSlot") {
      return (
        <LogoSlotEditor
          slot={draft.template.slots.logoSlot}
          disabled={disabled}
          onUpdate={(patch) =>
            onDraftChange((current) => updateLayoutTemplateLogoSlot(current, patch))
          }
        />
      );
    }

    return (
      <p className="text-xs text-muted-foreground">
        This layer is static. Bind it to a slot when planner content should replace or configure it.
      </p>
    );
  })();

  return (
    <div
      role="group"
      aria-label="Selected layer slot controls"
      className="space-y-3 rounded-md border bg-muted/20 p-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <FieldLabel>Slot Contract</FieldLabel>
          <p className="mt-1 text-xs text-muted-foreground">
            Current binding: {currentBindingLabel}
          </p>
        </div>
        {layer.type === "text" || layer.type === "image" ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={createAndBindSlot}
          >
            <Plus className="size-3.5" />
            New slot
          </Button>
        ) : null}
      </div>
      <label className="block space-y-1.5">
        <FieldLabel>Binding</FieldLabel>
        <Select
          aria-label="Selected layer slot binding"
          value={currentBindingValue}
          disabled={disabled}
          onChange={(event) => updateBinding(parseLayerBindingValue(event.target.value))}
        >
          {bindingOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </label>
      {slotFields}
    </div>
  );
};

const SelectedLayerInspector = ({
  draft,
  layer,
  disabled,
  outsideCanvas,
  onFrameChange,
  onBringIntoCanvas,
  onTextChange,
  onDraftChange,
}: {
  draft: LayoutTemplateDraft;
  layer: EditableLayoutTemplateLayer | null;
  disabled: boolean;
  outsideCanvas: boolean;
  onFrameChange: (changes: LayoutTemplateLayerFrameUpdate) => void;
  onBringIntoCanvas: () => void;
  onTextChange: (text: string) => void;
  onDraftChange: DraftUpdater;
}) => {
  if (!layer) {
    return <SelectedLayerEmptyState />;
  }

  return (
    <div className="space-y-4 rounded-md border bg-background/75 p-4">
      <SelectedLayerHeader
        layer={layer}
        outsideCanvas={outsideCanvas}
        disabled={disabled}
        onBringIntoCanvas={onBringIntoCanvas}
      />
      <LayerGeometryControls layer={layer} disabled={disabled} onFrameChange={onFrameChange} />
      {layer.type === "text" ? (
        <LayerTextControls
          layer={layer}
          disabled={disabled}
          onTextChange={onTextChange}
          onStyleChange={(changes) =>
            onDraftChange((current) =>
              updateLayoutTemplateTextLayerStyle(current, layer.id, changes),
            )
          }
        />
      ) : null}
      <SelectedLayerSlotControls
        draft={draft}
        layer={layer}
        disabled={disabled}
        onDraftChange={onDraftChange}
      />
      <LayerAiPolicyEditor layer={layer} disabled={disabled} onDraftChange={onDraftChange} />
    </div>
  );
};

const LayoutTemplateVisualEditor = ({
  draft,
  disabled = false,
  onDraftChange,
}: {
  draft: LayoutTemplateDraft;
  disabled?: boolean;
  onDraftChange: DraftUpdater;
}) => {
  const textEditorRef = useRef<HTMLTextAreaElement | null>(null);
  const suppressNextTextEditBlurRef = useRef(false);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [editingTextLayerId, setEditingTextLayerId] = useState<string | null>(null);
  const [editingTextValue, setEditingTextValue] = useState<string | null>(null);

  useEffect(() => {
    if (selectedLayerId && !findLayoutTemplateLayer(draft, selectedLayerId)) {
      setSelectedLayerId(null);
    }
  }, [draft, selectedLayerId]);

  useEffect(() => {
    if (editingTextLayerId && !findLayoutTemplateLayer(draft, editingTextLayerId)) {
      setEditingTextLayerId(null);
      setEditingTextValue(null);
    }
  }, [draft, editingTextLayerId]);

  useEffect(() => {
    if (!editingTextLayerId) {
      return;
    }

    textEditorRef.current?.focus();
    textEditorRef.current?.select();
  }, [editingTextLayerId]);

  const layerEntries = getSortedLayoutTemplateLayerEntries(draft);
  const selectedLayer = findLayoutTemplateLayer(draft, selectedLayerId);
  const editingTextLayer = findLayoutTemplateLayer(draft, editingTextLayerId);
  const activeTextEditLayer =
    editingTextLayer?.type === "text" && editingTextValue != null ? editingTextLayer : null;
  const canvas = draft.template.canonicalScene.canvas;
  const selectedLayerOutsideCanvas = selectedLayer
    ? isLayoutTemplateLayerOutsideCanvas(selectedLayer, canvas)
    : false;

  const updateLayerFrame = (layerId: string, changes: LayoutTemplateLayerFrameUpdate) => {
    onDraftChange((current) => updateLayoutTemplateLayerFrame(current, layerId, changes));
  };

  const moveLayer = (layerId: string, direction: LayoutTemplateLayerMoveDirection) => {
    setSelectedLayerId(layerId);
    onDraftChange((current) => moveLayoutTemplateLayer(current, layerId, direction));
  };

  const updateSelectedText = (text: string) => {
    if (!selectedLayer) {
      return;
    }

    onDraftChange((current) => updateLayoutTemplateLayerText(current, selectedLayer.id, text));
  };

  const startTextEdit = (layerId: string) => {
    const layer = findLayoutTemplateLayer(draft, layerId);
    if (layer?.type !== "text") {
      return;
    }

    suppressNextTextEditBlurRef.current = false;
    setSelectedLayerId(layerId);
    setEditingTextLayerId(layerId);
    setEditingTextValue(layer.text);
  };

  const closeTextEdit = () => {
    setEditingTextLayerId(null);
    setEditingTextValue(null);
  };

  const commitTextEdit = (text = editingTextValue) => {
    if (activeTextEditLayer && text != null) {
      onDraftChange((current) =>
        updateLayoutTemplateLayerText(current, activeTextEditLayer.id, text),
      );
    }

    suppressNextTextEditBlurRef.current = true;
    closeTextEdit();
  };

  const cancelTextEdit = () => {
    suppressNextTextEditBlurRef.current = true;
    closeTextEdit();
  };

  const handleTextEditBlur = (text: string) => {
    if (suppressNextTextEditBlurRef.current) {
      suppressNextTextEditBlurRef.current = false;
      return;
    }

    commitTextEdit(text);
  };

  const bringSelectedLayerIntoCanvas = () => {
    if (!selectedLayer) {
      return;
    }

    updateLayerFrame(
      selectedLayer.id,
      resolveLayoutTemplateLayerCanvasRecoveryFrame(selectedLayer, canvas),
    );
  };

  return (
    <div className="space-y-4">
      <div
        role="group"
        aria-label="Canonical scene editor"
        className="rounded-md border bg-background/80 p-3 shadow-xs"
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div>
            <span className="font-medium text-foreground">Canonical Scene Editor</span>
            <div className="mt-1 text-muted-foreground">
              Drag, resize, edit copy, then save the template draft.
            </div>
          </div>
          <span className="font-mono text-[11px] text-muted-foreground">
            {canvas.width} x {canvas.height}
          </span>
        </div>

        <LayoutTemplateCanvasSurface
          template={draft.template}
          onBackgroundSelect={() => setSelectedLayerId(null)}
          renderCanvas={({ scale, sortedLayers }) =>
            sortedLayers.map((layer) => (
              <EditableLayoutTemplateLayerNode
                key={layer.id}
                layer={layer}
                scale={scale}
                selected={layer.id === selectedLayerId}
                disabled={disabled}
                editing={layer.id === editingTextLayerId}
                onSelect={setSelectedLayerId}
                onStartTextEdit={startTextEdit}
                onFrameChange={updateLayerFrame}
              />
            ))
          }
          renderOverlay={({ pasteboardFrame, scale }) =>
            activeTextEditLayer ? (
              <textarea
                ref={textEditorRef}
                aria-label="Edit layer text"
                value={editingTextValue ?? ""}
                rows={1}
                className="absolute z-10 resize-none overflow-hidden rounded-md border border-primary bg-background/95 p-0 text-foreground shadow-lg outline-none ring-2 ring-primary/20"
                style={{
                  left: pasteboardFrame.canvasX + activeTextEditLayer.x * scale,
                  top: pasteboardFrame.canvasY + activeTextEditLayer.y * scale,
                  width: activeTextEditLayer.width * scale,
                  height: activeTextEditLayer.height * scale,
                  color: activeTextEditLayer.fill,
                  fontFamily: activeTextEditLayer.fontFamily,
                  fontSize: activeTextEditLayer.fontSize * scale,
                  fontStyle: activeTextEditLayer.fontStyle.includes("italic") ? "italic" : "normal",
                  fontWeight: activeTextEditLayer.fontStyle.includes("bold") ? 700 : 400,
                  lineHeight: activeTextEditLayer.lineHeight,
                  letterSpacing: activeTextEditLayer.letterSpacing * scale,
                  padding: activeTextEditLayer.padding * scale,
                  textAlign: activeTextEditLayer.align,
                  boxSizing: "border-box",
                  transform: `rotate(${activeTextEditLayer.rotation}deg)`,
                  transformOrigin: "top left",
                }}
                onBlur={(event) => handleTextEditBlur(event.currentTarget.value)}
                onChange={(event) => setEditingTextValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    event.preventDefault();
                    cancelTextEdit();
                    return;
                  }

                  if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                    event.preventDefault();
                    commitTextEdit(event.currentTarget.value);
                  }
                }}
              />
            ) : null
          }
        />

        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <MousePointer2 className="size-3.5" />
          Select a layer before dragging for precision; use the inspector for exact values.
        </div>
      </div>

      <div className="grid gap-4 2xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)]">
        <div className="rounded-md border bg-background/75 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <FieldLabel>Layer Stack</FieldLabel>
              <div className="mt-1 text-sm font-medium text-foreground">Front to back</div>
            </div>
            <Badge variant="secondary">{layerEntries.length}</Badge>
          </div>

          <div className="mt-3 max-h-80 space-y-2 overflow-y-auto pr-1">
            {layerEntries.map((entry, index) => {
              const selected = entry.id === selectedLayerId;
              const isFront = index === 0;
              const isBack = index === layerEntries.length - 1;

              return (
                <div
                  key={entry.id}
                  className={cn(
                    "rounded-md border p-2 transition-colors",
                    selected ? "border-primary bg-primary/5" : "bg-card hover:bg-muted/30",
                  )}
                >
                  <button
                    type="button"
                    className="block w-full text-left"
                    onClick={() => setSelectedLayerId(entry.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-foreground">
                          {entry.label}
                        </div>
                        <div className="mt-1 font-mono text-[11px] text-muted-foreground">
                          {entry.id}
                        </div>
                      </div>
                      <span className="rounded-full border bg-background px-2 py-0.5 text-[10px] text-muted-foreground">
                        {layoutTemplateLayerTypeLabel[entry.type]}
                      </span>
                    </div>
                  </button>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="font-mono text-[11px] text-muted-foreground">
                      z{entry.zIndex} {entry.visible ? "visible" : "hidden"}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={`Move ${entry.label} to front`}
                        disabled={disabled || isFront}
                        onClick={() => moveLayer(entry.id, "front")}
                      >
                        <ChevronsUp className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={`Move ${entry.label} forward`}
                        disabled={disabled || isFront}
                        onClick={() => moveLayer(entry.id, "forward")}
                      >
                        <ArrowUp className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={`Move ${entry.label} backward`}
                        disabled={disabled || isBack}
                        onClick={() => moveLayer(entry.id, "backward")}
                      >
                        <ArrowDown className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={`Move ${entry.label} to back`}
                        disabled={disabled || isBack}
                        onClick={() => moveLayer(entry.id, "back")}
                      >
                        <ChevronsDown className="size-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <SelectedLayerInspector
          draft={draft}
          layer={selectedLayer}
          disabled={disabled}
          outsideCanvas={selectedLayerOutsideCanvas}
          onDraftChange={onDraftChange}
          onBringIntoCanvas={bringSelectedLayerIntoCanvas}
          onFrameChange={(changes) => {
            if (selectedLayer) {
              updateLayerFrame(selectedLayer.id, changes);
            }
          }}
          onTextChange={updateSelectedText}
        />
      </div>
    </div>
  );
};

export function LayoutTemplatesPage() {
  const utils = trpc.useUtils();
  const importFileInputRef = useRef<HTMLInputElement | null>(null);
  const templatesQuery = trpc.admin.socialPostLayoutTemplates.list.useQuery({
    publishedOnly: false,
  });
  const exportBundleQuery = trpc.admin.socialPostLayoutTemplates.exportBundle.useQuery(undefined, {
    enabled: false,
  });
  const usageQuery = trpc.admin.brands.socialPostLayoutTemplateUsage.useQuery({});
  const templates = useMemo(() => templatesQuery.data ?? [], [templatesQuery.data]);
  const [draft, setDraft] = useState<LayoutTemplateDraft | null>(null);
  const [activeEditorTab, setActiveEditorTab] = useState<LayoutTemplateEditorTab>("overview");
  const [rawJson, setRawJson] = useState("");
  const [rawJsonError, setRawJsonError] = useState<string | null>(null);
  const [rawJsonSyncRevision, setRawJsonSyncRevision] = useState(0);
  const syncedRawJsonRevisionRef = useRef(0);
  const [transferError, setTransferError] = useState<string | null>(null);

  const selectedTemplate = draft?.id
    ? (templates.find((template) => template.id === draft.id) ?? null)
    : null;

  const revisionsQuery = trpc.admin.socialPostLayoutTemplates.listRevisions.useQuery(
    { id: selectedTemplate?.id ?? "00000000-0000-4000-8000-000000000000" },
    { enabled: Boolean(selectedTemplate?.id) },
  );

  const setSynchronizedDraft = (nextDraft: LayoutTemplateDraft | null) => {
    setDraft(nextDraft);
    setRawJson(nextDraft ? formatLayoutTemplateRawJsonDraft(nextDraft) : "");
    setRawJsonError(null);
  };

  const requestRawJsonSync = () => setRawJsonSyncRevision((revision) => revision + 1);

  useEffect(() => {
    setDraft((current) =>
      reconcileLayoutTemplateDraftWithTemplates({
        currentDraft: current,
        templates,
      }),
    );
  }, [templates]);

  useEffect(() => {
    if (!draft) {
      setRawJson("");
      setRawJsonError(null);
      syncedRawJsonRevisionRef.current = rawJsonSyncRevision;
      return;
    }

    const shouldSyncRawJson =
      activeEditorTab !== "raw" || syncedRawJsonRevisionRef.current !== rawJsonSyncRevision;
    if (shouldSyncRawJson) {
      setRawJson(formatLayoutTemplateRawJsonDraft(draft));
      setRawJsonError(null);
      syncedRawJsonRevisionRef.current = rawJsonSyncRevision;
    }
  }, [activeEditorTab, draft, rawJsonSyncRevision]);

  const validationErrors = useMemo(
    () => (draft ? validateLayoutTemplateDraft(draft) : []),
    [draft],
  );
  const isEditing = Boolean(selectedTemplate);

  const invalidateTemplates = async (
    revisionTemplateId: string | null = selectedTemplate?.id ?? null,
  ) => {
    await utils.admin.socialPostLayoutTemplates.list.invalidate({ publishedOnly: false });
    await utils.admin.brands.socialPostLayoutTemplateUsage.invalidate({});
    if (revisionTemplateId) {
      await utils.admin.socialPostLayoutTemplates.listRevisions.invalidate({
        id: revisionTemplateId,
      });
    }
  };

  const createTemplate = trpc.admin.socialPostLayoutTemplates.create.useMutation({
    onSuccess: async (template) => {
      setSynchronizedDraft(createLayoutTemplateDraftFromOutput(template));
      await invalidateTemplates();
    },
  });
  const updateDraft = trpc.admin.socialPostLayoutTemplates.updateDraft.useMutation({
    onSuccess: async (template) => {
      setSynchronizedDraft(createLayoutTemplateDraftFromOutput(template));
      await invalidateTemplates();
    },
  });
  const publishTemplate = trpc.admin.socialPostLayoutTemplates.publish.useMutation({
    onSuccess: async (template) => {
      setSynchronizedDraft(createLayoutTemplateDraftFromOutput(template));
      await invalidateTemplates();
    },
  });
  const archiveTemplate = trpc.admin.socialPostLayoutTemplates.archive.useMutation({
    onSuccess: async (template) => {
      setSynchronizedDraft(createLayoutTemplateDraftFromOutput(template));
      await invalidateTemplates();
    },
  });
  const deleteTemplate = trpc.admin.socialPostLayoutTemplates.deletePermanently.useMutation({
    onSuccess: async (deletedTemplate) => {
      const nextDraft = resolveLayoutTemplateDraftAfterDelete({
        currentDraft: draft,
        deletedTemplateId: deletedTemplate.id,
        templates,
      });

      setSynchronizedDraft(nextDraft);
      await invalidateTemplates(nextDraft?.id ?? null);
    },
  });
  const rollbackTemplate = trpc.admin.socialPostLayoutTemplates.rollback.useMutation({
    onSuccess: async (template) => {
      setSynchronizedDraft(createLayoutTemplateDraftFromOutput(template));
      await invalidateTemplates();
    },
  });
  const duplicateTemplate = trpc.admin.socialPostLayoutTemplates.duplicate.useMutation({
    onSuccess: async (template) => {
      setSynchronizedDraft(createLayoutTemplateDraftFromOutput(template));
      await invalidateTemplates();
    },
  });
  const importBundle = trpc.admin.socialPostLayoutTemplates.importBundle.useMutation({
    onSuccess: async (result) => {
      const firstImportedTemplate = result.templates[0];
      if (firstImportedTemplate) {
        setSynchronizedDraft(createLayoutTemplateDraftFromOutput(firstImportedTemplate));
      }
      await invalidateTemplates();
    },
  });
  const reorderTemplates = trpc.admin.socialPostLayoutTemplates.reorder.useMutation({
    onSuccess: async (orderedTemplates) => {
      const reorderedSelectedTemplate = draft?.id
        ? orderedTemplates.find((template) => template.id === draft.id)
        : null;
      if (reorderedSelectedTemplate && reorderedSelectedTemplate.sortOrder !== draft?.sortOrder) {
        requestRawJsonSync();
      }

      setDraft((current) => {
        if (!current?.id) {
          return current;
        }

        const updatedTemplate = orderedTemplates.find((template) => template.id === current.id);
        return updatedTemplate ? { ...current, sortOrder: updatedTemplate.sortOrder } : current;
      });
      await invalidateTemplates();
    },
  });

  const mutationError =
    createTemplate.error?.message ??
    updateDraft.error?.message ??
    publishTemplate.error?.message ??
    archiveTemplate.error?.message ??
    deleteTemplate.error?.message ??
    rollbackTemplate.error?.message ??
    duplicateTemplate.error?.message ??
    reorderTemplates.error?.message ??
    null;
  const transferErrorMessage = importBundle.error?.message ?? transferError;
  const isMutating =
    createTemplate.isPending ||
    updateDraft.isPending ||
    publishTemplate.isPending ||
    archiveTemplate.isPending ||
    deleteTemplate.isPending ||
    rollbackTemplate.isPending ||
    duplicateTemplate.isPending ||
    importBundle.isPending ||
    reorderTemplates.isPending;
  const isExporting = exportBundleQuery.isFetching;

  const updateDraftField = <TKey extends keyof LayoutTemplateDraft>(
    key: TKey,
    value: LayoutTemplateDraft[TKey],
  ) => setDraft((current) => (current ? { ...current, [key]: value } : current));

  const updateDraftState: DraftUpdater = (updater) =>
    setDraft((current) => (current ? updater(current) : current));

  const selectTemplate = (template: LayoutTemplate) => {
    setSynchronizedDraft(createLayoutTemplateDraftFromOutput(template));
    setActiveEditorTab("overview");
  };

  const startNewTemplate = () => {
    setSynchronizedDraft(
      createEmptyLayoutTemplateDraft(
        templates.map((template) => template.templateId),
        getNextLayoutTemplateSortOrder(templates),
      ),
    );
    setActiveEditorTab("overview");
  };

  const updateRawJsonDraft = (nextRawJson: string) => {
    setRawJson(nextRawJson);

    if (!draft) {
      setRawJsonError(null);
      return;
    }

    const result = parseLayoutTemplateRawJsonDraft(nextRawJson, draft);
    if (!result.valid) {
      setRawJsonError(result.error);
      return;
    }

    setRawJsonError(null);
    setDraft(result.draft);
  };

  const resetRawJsonDraft = () => {
    if (!draft) {
      setRawJson("");
      setRawJsonError(null);
      return;
    }

    setRawJson(formatLayoutTemplateRawJsonDraft(draft));
    setRawJsonError(null);
  };

  const exportLayoutTemplates = async () => {
    setTransferError(null);
    const result = await exportBundleQuery.refetch();

    if (result.error) {
      setTransferError(result.error.message);
      return;
    }

    if (result.data) {
      downloadLayoutTemplateExportBundle(result.data);
    }
  };

  const importLayoutTemplates = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    input.value = "";

    if (!file) {
      return;
    }

    setTransferError(null);

    try {
      const parsedBundle = socialPostLayoutTemplateExportBundleSchema.parse(
        JSON.parse(await file.text()),
      );
      await importBundle.mutateAsync(parsedBundle);
    } catch (error) {
      setTransferError(error instanceof Error ? error.message : "Layout template import failed.");
    }
  };

  const saveDraft = () => {
    if (!draft || rawJsonError) {
      return;
    }

    const payload = buildLayoutTemplateWriteInput(draft);
    if (draft.id) {
      updateDraft.mutate({ id: draft.id, ...payload });
      return;
    }

    createTemplate.mutate(payload);
  };

  const publishSelectedTemplate = async () => {
    if (!selectedTemplate?.id || !draft || rawJsonError) {
      return;
    }

    const payload = buildLayoutTemplateWriteInput(draft);
    try {
      await updateDraft.mutateAsync({ id: selectedTemplate.id, ...payload });
    } catch {
      return;
    }

    publishTemplate.mutate({ id: selectedTemplate.id });
  };

  const archiveSelectedTemplate = () => {
    if (selectedTemplate?.id) {
      archiveTemplate.mutate({ id: selectedTemplate.id });
    }
  };

  const deleteSelectedTemplate = () => {
    if (!selectedTemplate) {
      return;
    }

    const confirmed = window.confirm(
      `Permanently delete "${selectedTemplate.name}"?\n\nThis also deletes its revision history. Existing generated assets and historical posts remain untouched. This cannot be undone.`,
    );
    if (!confirmed) {
      return;
    }

    deleteTemplate.mutate({ id: selectedTemplate.id });
  };

  const duplicateSelectedTemplate = () => {
    if (!selectedTemplate) {
      return;
    }

    duplicateTemplate.mutate({
      id: selectedTemplate.id,
      templateId: getDuplicateTemplateId(),
      name: getDuplicateName(selectedTemplate),
    });
  };

  const rollbackSelectedTemplate = (revision: LayoutTemplateRevision) => {
    if (selectedTemplate?.id) {
      rollbackTemplate.mutate({ id: selectedTemplate.id, revisionNumber: revision.revisionNumber });
    }
  };

  const reorder = (templateId: string, direction: -1 | 1) => {
    const ids = templates.map((template) => template.id);
    const orderedIds = moveLayoutTemplateId(ids, templateId, direction);
    if (orderedIds.join("|") !== ids.join("|")) {
      reorderTemplates.mutate({ ids: orderedIds });
    }
  };

  if (templatesQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (templatesQuery.error) {
    return (
      <QueryErrorState
        title="Layout templates unavailable"
        message={templatesQuery.error.message}
        isForbidden={templatesQuery.error.data?.code === "FORBIDDEN"}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Layout Templates</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Manage the published template library used by social post planning. This first editor
            covers template metadata, validation, publishing, revisions, and ordering.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={importFileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            aria-label="Import layout templates file"
            onChange={importLayoutTemplates}
          />
          <Button
            onClick={exportLayoutTemplates}
            variant="outline"
            disabled={templates.length === 0 || isExporting}
          >
            <Download className="size-4" />
            {isExporting ? "Exporting..." : "Export JSON"}
          </Button>
          <Button
            onClick={() => importFileInputRef.current?.click()}
            variant="outline"
            disabled={importBundle.isPending}
          >
            <Upload className="size-4" />
            {importBundle.isPending ? "Importing..." : "Import JSON"}
          </Button>
          <Button onClick={startNewTemplate} variant="outline">
            <Plus className="size-4" />
            New template
          </Button>
        </div>
      </div>

      {transferErrorMessage ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
        >
          {transferErrorMessage}
        </p>
      ) : null}

      <div className="grid gap-5 xl:grid-cols-[minmax(300px,380px)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Template Library</CardTitle>
            <CardDescription>
              {templates.length} draft, published, or archived templates
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {templates.length === 0 ? (
              <EmptyState
                title="No layout templates"
                description="Create the first admin-authored template draft."
                actions={
                  <Button size="sm" onClick={startNewTemplate}>
                    <Plus className="size-3.5" />
                    Create draft
                  </Button>
                }
              />
            ) : (
              templates.map((template, index) => (
                <div
                  key={template.id}
                  role="group"
                  aria-label={`Template card ${template.name}`}
                  className={cn(
                    "rounded-lg border p-2.5 transition-colors",
                    draft?.id === template.id ? "border-primary bg-primary/5" : "bg-background",
                  )}
                >
                  <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-3">
                    <button
                      type="button"
                      className="rounded-lg text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      aria-label={`Select ${template.name}`}
                      onClick={() => selectTemplate(template)}
                    >
                      <LayoutTemplateCanvasPreview
                        template={template.draftTemplate}
                        label={`Template thumbnail for ${template.name}`}
                        className="h-[6.25rem] w-20"
                        maxWidth={80}
                      />
                    </button>
                    <div className="min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          onClick={() => selectTemplate(template)}
                        >
                          <span className="block truncate text-sm font-medium">
                            {template.name}
                          </span>
                        </button>
                        <StatusBadge status={template.status} />
                      </div>
                      <div className="mt-2 text-xs leading-5 text-muted-foreground">
                        <LayoutTemplateUsageSummary
                          metric={getLayoutTemplateUsageMetric(
                            usageQuery.data,
                            template.templateId,
                          )}
                          isLoading={usageQuery.isPending}
                          isError={usageQuery.isError}
                        />
                        {template.hasUnpublishedChanges ? (
                          <span className="block font-medium text-amber-700">
                            Unpublished changes
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => selectTemplate(template)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Move ${template.name} up`}
                          disabled={index === 0 || isMutating}
                          onClick={() => reorder(template.id, -1)}
                        >
                          <ArrowUp className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Move ${template.name} down`}
                          disabled={index === templates.length - 1 || isMutating}
                          onClick={() => reorder(template.id, 1)}
                        >
                          <ArrowDown className="size-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">
                  {isEditing ? "Edit Layout Template" : "Create Layout Template"}
                </CardTitle>
                <CardDescription>
                  Published snapshots are immutable. Draft edits are validated before they can be
                  saved or published.
                </CardDescription>
              </div>
              {draft ? <StatusBadge status={draft.status} /> : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {!draft ? (
              <EmptyState
                title="Select or create a template"
                description="Choose a template from the library or start a blank draft."
                actions={
                  <Button size="sm" onClick={startNewTemplate}>
                    <Plus className="size-3.5" />
                    New template
                  </Button>
                }
              />
            ) : (
              <>
                <Tabs
                  value={activeEditorTab}
                  onValueChange={(value) => setActiveEditorTab(value as LayoutTemplateEditorTab)}
                >
                  <TabsList className="grid h-auto w-full grid-cols-2 lg:grid-cols-5">
                    {LAYOUT_TEMPLATE_EDITOR_TABS.map((tab) => (
                      <TabsTrigger key={tab.value} value={tab.value} className="py-2 text-xs">
                        {tab.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  <TabsContent value="overview" className="pt-3">
                    <LayoutTemplateOverviewSection
                      draft={draft}
                      onDraftFieldChange={updateDraftField}
                      onDraftChange={updateDraftState}
                    />
                  </TabsContent>

                  <TabsContent value="visual" className="space-y-4 pt-3">
                    <div className="grid gap-3 md:grid-cols-3">
                      <TemplateStat
                        label="Canvas"
                        value={`${draft.template.canonicalScene.canvas.width}x${draft.template.canonicalScene.canvas.height}`}
                      />
                      <TemplateStat
                        label="Layers"
                        value={draft.template.canonicalScene.layers.length}
                      />
                      <TemplateStat
                        label="Slots"
                        value={`${draft.template.slots.textSlots.length} text / ${draft.template.slots.imageSlots.length} image`}
                      />
                    </div>
                    <LayoutTemplateVisualEditor
                      draft={draft}
                      disabled={isMutating}
                      onDraftChange={updateDraftState}
                    />
                  </TabsContent>

                  <TabsContent value="slots" className="pt-3">
                    <SlotEditorSection draft={draft} onDraftChange={updateDraftState} />
                  </TabsContent>

                  <TabsContent value="raw" className="pt-3">
                    <LayoutTemplateRawJsonSection
                      rawJson={rawJson}
                      error={rawJsonError}
                      disabled={isMutating}
                      onRawJsonChange={updateRawJsonDraft}
                      onReset={resetRawJsonDraft}
                    />
                  </TabsContent>

                  <TabsContent value="publish" className="space-y-4 pt-3">
                    <div className="rounded-md border bg-muted/30 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <FieldLabel>Validation</FieldLabel>
                          <p className="text-xs text-muted-foreground">
                            Server-side validation is still enforced when saving and publishing.
                          </p>
                        </div>
                        <Badge variant={validationErrors.length === 0 ? "default" : "destructive"}>
                          {validationErrors.length === 0
                            ? "Ready"
                            : `${validationErrors.length} issue(s)`}
                        </Badge>
                      </div>
                      {validationErrors.length > 0 ? (
                        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-destructive">
                          {validationErrors.map((error) => (
                            <li key={error}>{error}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-3 text-sm text-muted-foreground">
                          This draft passes the shared Layout Template schema.
                        </p>
                      )}
                    </div>

                    {selectedTemplate ? (
                      <div className="rounded-md border p-3">
                        <div className="flex items-center gap-2">
                          <History className="size-4 text-muted-foreground" />
                          <FieldLabel>Published Revisions</FieldLabel>
                        </div>
                        {revisionsQuery.isPending ? (
                          <div className="mt-3 space-y-2">
                            <Skeleton className="h-8 w-full" />
                            <Skeleton className="h-8 w-3/4" />
                          </div>
                        ) : revisionsQuery.data && revisionsQuery.data.length > 0 ? (
                          <div className="mt-3 space-y-2">
                            {revisionsQuery.data.map((revision) => (
                              <div
                                key={revision.id}
                                className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2 text-sm"
                              >
                                <div>
                                  <div className="font-medium">
                                    Revision {revision.revisionNumber}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    Published {formatDateTime(revision.publishedAt)}
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={isMutating}
                                  onClick={() => rollbackSelectedTemplate(revision)}
                                >
                                  <RotateCcw className="size-3.5" />
                                  Roll back
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-3 text-sm text-muted-foreground">
                            Publish this draft to create the first immutable revision.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </TabsContent>
                </Tabs>

                {mutationError ? <p className="text-sm text-destructive">{mutationError}</p> : null}

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    onClick={saveDraft}
                    disabled={validationErrors.length > 0 || Boolean(rawJsonError) || isMutating}
                  >
                    <Save className="size-4" />
                    {isEditing ? "Save draft" : "Create template"}
                  </Button>
                  {selectedTemplate ? (
                    <Button
                      variant="outline"
                      disabled={validationErrors.length > 0 || Boolean(rawJsonError) || isMutating}
                      onClick={publishSelectedTemplate}
                    >
                      <Send className="size-4" />
                      Publish
                    </Button>
                  ) : null}
                  {selectedTemplate && selectedTemplate.status !== "archived" ? (
                    <Button
                      variant="outline"
                      disabled={isMutating}
                      onClick={archiveSelectedTemplate}
                    >
                      <Archive className="size-4" />
                      Archive
                    </Button>
                  ) : null}
                  {selectedTemplate ? (
                    <Button
                      variant="outline"
                      disabled={isMutating}
                      onClick={duplicateSelectedTemplate}
                    >
                      <Copy className="size-4" />
                      Duplicate
                    </Button>
                  ) : null}
                  {selectedTemplate ? (
                    <Button
                      variant="destructive"
                      disabled={isMutating}
                      onClick={deleteSelectedTemplate}
                    >
                      <Trash2 className="size-4" />
                      Delete permanently
                    </Button>
                  ) : null}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
